import { Form, Input, message, Modal, Button } from 'antd';
import LOCALS from 'commons/locals';
import { useEffect, useState } from 'react';
import i18n from 'i18n';
import FormImageUpload from 'components/form-image-upload';
import { CmsApi, CmsSpecialContent } from 'apis/cms';

interface Props {
  open: boolean;
  onClose: () => void;
  data?: CmsSpecialContent;
  onLoad?: () => void;
}

// 自定义图片上传组件，处理图片布局和交互
const CustomImageUpload = ({
  value,
  onChange,
}: {
  value?: string;
  onChange?: (value: string) => void;
}) => {
  // 将字符串值转换为数组格式以便显示
  const images = value ? value.split(',') : [];

  // 处理从FormImageUpload上传后的回调
  const handleUpload = (newValue: string | string[]) => {
    if (onChange) {
      // 获取新上传的图片URL
      let newImageUrl = '';
      if (typeof newValue === 'string') {
        newImageUrl = newValue;
      } else if (Array.isArray(newValue) && newValue.length > 0) {
        newImageUrl = newValue[0];
      }

      if (newImageUrl) {
        // 添加新图片到现有图片列表
        const newImages = [...images, newImageUrl];
        // 转换回逗号分隔的字符串并更新表单值
        onChange(newImages.join(','));
      }
    }
  };

  return (
    <div>
      {/* 显示所有已上传的图片 */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '16px',
          width: '100%',
        }}
      >
        {images.map((url, index) => (
          <div
            key={index}
            style={{
              width: '140px',
              height: '140px',
              border: '1px dashed #d9d9d9',
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
            }}
          >
            <img
              src={url}
              alt={`图片${index + 1}`}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />

            {/* 删除按钮 */}
            <div
              style={{
                position: 'absolute',
                top: '5px',
                right: '5px',
                width: '20px',
                height: '20px',
                backgroundColor: 'rgba(255, 0, 0, 0.7)',
                color: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '14px',
                lineHeight: 1,
              }}
              onClick={() => {
                if (onChange) {
                  // 删除当前图片
                  const newImages = [...images];
                  newImages.splice(index, 1);
                  onChange(newImages.join(','));
                }
              }}
            >
              ×
            </div>
          </div>
        ))}

        {/* 上传按钮 */}
        {images.length < 5 && (
          <div
            style={{
              width: '140px',
              height: '140px',
              border: '1px dashed #d9d9d9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FormImageUpload
              value={undefined}
              onChange={handleUpload}
              maxCount={1}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const CmsSpecialContentEditModal = ({ open, onClose, data, onLoad }: Props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formValid, setFormValid] = useState(false);

  // 检查表单是否有效
  const checkFormValid = async () => {
    try {
      // 获取所有必填字段的值
      const fieldsValue = form.getFieldsValue([
        'productName',
        'rank',
        'pic',
        'productId',
        'arrivalDate',
        'path',
      ]);

      // 检查是否所有必填字段都有值
      const isValid =
        fieldsValue.productName &&
        fieldsValue.rank &&
        fieldsValue.pic &&
        fieldsValue.productId &&
        fieldsValue.path &&
        fieldsValue.arrivalDate;

      setFormValid(!!isValid);
    } catch (e) {
      setFormValid(false);
    }
  };

  // 表单值变化时检查有效性
  const handleFormChange = () => {
    checkFormValid();
  };

  const onFinish = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();

      // 将productId字符串转换为数字
      let productId: number | null = null;
      if (values.productId) {
        productId = Number(values.productId);
        // 确保是有效的数字
        if (isNaN(productId)) {
          productId = null;
        }
      }

      await CmsApi.addOrUpdateSpecialContent({
        ...values,
        productId,
      });

      message.success(i18n.t(LOCALS.successful_operation));
      onLoad?.();
      onClose();
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data) {
      const formData = {
        ...data,
        productId:
          data.productId !== undefined ? String(data.productId) : undefined,
      };

      form.setFieldsValue(formData);
      // 编辑模式下，检查表单初始状态
      setTimeout(checkFormValid, 0);
    } else {
      form.resetFields();
      setFormValid(false);
    }
  }, [data, form]);

  return (
    <Modal
      open={open}
      title={data ? i18n.t('edit') : i18n.t('add')}
      onCancel={() => onClose()}
      footer={[
        <Button key="cancel" onClick={() => onClose()}>
          {i18n.t('cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          disabled={!formValid}
          onClick={onFinish}
        >
          {i18n.t('ok')}
        </Button>,
      ]}
      destroyOnClose
      width={600}
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 16 }}
        onValuesChange={handleFormChange}
      >
        <Form.Item name="id" hidden>
          <Input />
        </Form.Item>

        <Form.Item label={i18n.t('product_name')} name="productName" required>
          <Input maxLength={100} />
        </Form.Item>

        <Form.Item label={i18n.t('rank')} name="rank" required>
          <Input maxLength={50} />
        </Form.Item>

        <Form.Item label={i18n.t('product_pictures')} name="pic" required>
          <CustomImageUpload />
        </Form.Item>

        <Form.Item
          label={i18n.t('associated_product_id')}
          name="productId"
          required
          rules={[
            {
              required: true,
            },
            {
              pattern: /^\d+$/,
            },
          ]}
        >
          <Input
            style={{ width: '100%' }}
            maxLength={20}
            type="number" // 使用HTML5的number类型
            onChange={(e) => {
              const value = e.target.value;
              // 如果输入不是数字，清空输入框
              if (value !== '' && !/^\d+$/.test(value)) {
                e.target.value = value.replace(/\D/g, '');
              }
            }}
          />
        </Form.Item>

        <Form.Item label={i18n.t('BjbkDXQZio')} name="arrivalDate" required>
          <Input maxLength={50} placeholder="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item label={i18n.t('path')} name="path" required>
          <Input maxLength={100} />
        </Form.Item>

        <Form.Item label={i18n.t('stamp')} name="stamp">
          <Input maxLength={50} />
        </Form.Item>

        <Form.Item label={i18n.t('description')} name="description">
          <Input.TextArea maxLength={500} rows={4} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CmsSpecialContentEditModal;
